import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useWallet } from '../WalletProvider'
import { useDemo } from '../DemoProvider'
import Logo from '../Logo'

const Nav = styled.nav`
  position: fixed;
  top: ${props => props.$isDemoMode ? '40px' : '0'};
  left: 0;
  right: 0;
  height: var(--nav-height);
  background: rgba(10, 11, 15, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 var(--container-padding-desktop);
  transition: top 0.3s ease;

  @media (max-width: 1024px) {
    padding: 0 var(--container-padding-tablet);
  }

  @media (max-width: 768px) {
    padding: 0 var(--container-padding-mobile);
    height: var(--nav-height-mobile);
  }
`

const LogoLink = styled(Link)`
  margin-right: 3rem;
  text-decoration: none;
  color: var(--color-text);
  z-index: 1002;
  
  &:hover {
    color: var(--color-primary);
  }

  @media (max-width: 768px) {
    margin-right: 0;
  }
`

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--color-background);
    flex-direction: column;
    justify-content: center;
    gap: 2.5rem;
    transform: translateX(${props => props.$isOpen ? '0' : '100%'});
    opacity: ${props => props.$isOpen ? '1' : '0'};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1001;
    padding: 5rem 2rem;
  }
`

const NavLink = styled(Link)`
  font-weight: 500;
  color: ${props => props.$active ? 'var(--color-primary)' : 'var(--color-text)'};
  position: relative;
  padding: 0.5rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.$active ? '100%' : '0'};
    height: 2px;
    background-color: var(--color-primary);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 1rem;
    width: 100%;
    text-align: center;

    &::after {
      bottom: 0.5rem;
    }
  }
`

const WalletContainer = styled.div`
  margin-left: auto;

  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
    display: flex;
    justify-content: center;
  }
`

const MenuButton = styled.button`
  display: none;
  position: relative;
  width: 30px;
  height: 30px;
  margin-left: auto;
  z-index: 1002;
  padding: 5px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }

  span {
    display: block;
    width: 100%;
    height: 2px;
    background-color: var(--color-text);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: 1px;

    &:first-child {
      transform: ${props => props.$isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      opacity: ${props => props.$isOpen ? '0' : '1'};
      transform: ${props => props.$isOpen ? 'translateX(20px)' : 'translateX(0)'};
    }

    &:last-child {
      transform: ${props => props.$isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }

  &:hover span {
    background-color: var(--color-primary);
  }
`

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  opacity: ${props => props.$isOpen ? '1' : '0'};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;

  @media (max-width: 768px) {
    display: block;
  }
`

function Navigation() {
  const location = useLocation()
  const { WalletControl } = useWallet()
  const { isDemoMode } = useDemo()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])
  
  return (
    <>
      <Nav $isDemoMode={isDemoMode}>
        <LogoLink to="/">
          <Logo />
        </LogoLink>
        <MenuButton 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          $isOpen={isMenuOpen}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span />
          <span />
          <span />
        </MenuButton>
        <NavLinks $isOpen={isMenuOpen}>
          <NavLink 
            to="/" 
            $active={location.pathname === '/'}
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </NavLink>
          <NavLink 
            to="/request" 
            $active={location.pathname === '/request'}
            onClick={() => setIsMenuOpen(false)}
          >
            Request Project
          </NavLink>
          <NavLink 
            to="/projects" 
            $active={location.pathname === '/projects'}
            onClick={() => setIsMenuOpen(false)}
          >
            Projects
          </NavLink>
          <NavLink 
            to="/whitepaper" 
            $active={location.pathname === '/whitepaper'}
            onClick={() => setIsMenuOpen(false)}
          >
            Whitepaper
          </NavLink>
          <NavLink 
            to="/guide" 
            $active={location.pathname === '/guide'}
            onClick={() => setIsMenuOpen(false)}
          >
            Guide
          </NavLink>
          <NavLink 
            to="/api" 
            $active={location.pathname === '/api'}
            onClick={() => setIsMenuOpen(false)}
          >
            API
          </NavLink>
          <WalletContainer>
            <WalletControl />
          </WalletContainer>
        </NavLinks>
      </Nav>
      <Overlay 
        $isOpen={isMenuOpen} 
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
    </>
  )
}

export default Navigation
